package com.partejar.app.core.ui.theme

import com.composables.icons.lucide.ArrowLeft
import com.composables.icons.lucide.Bell
import com.composables.icons.lucide.Building2
import com.composables.icons.lucide.CalendarDays
import com.composables.icons.lucide.ChevronRight
import com.composables.icons.lucide.CircleAlert
import com.composables.icons.lucide.CircleCheck
import com.composables.icons.lucide.CircleOff
import com.composables.icons.lucide.Clipboard
import com.composables.icons.lucide.Clock
import com.composables.icons.lucide.EllipsisVertical
import com.composables.icons.lucide.Eye
import com.composables.icons.lucide.EyeOff
import com.composables.icons.lucide.FingerprintPattern
import com.composables.icons.lucide.FileText
import com.composables.icons.lucide.HeartHandshake
import com.composables.icons.lucide.Handshake
import com.composables.icons.lucide.House
import com.composables.icons.lucide.Info
import com.composables.icons.lucide.Instagram
import com.composables.icons.lucide.LogOut
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Moon
import com.composables.icons.lucide.MessageCircle
import com.composables.icons.lucide.PencilLine
import com.composables.icons.lucide.Plus
import com.composables.icons.lucide.Search
import com.composables.icons.lucide.Send
import com.composables.icons.lucide.Settings
import com.composables.icons.lucide.RefreshCw
import com.composables.icons.lucide.Sun
import com.composables.icons.lucide.SquareActivity
import com.composables.icons.lucide.Sunrise
import com.composables.icons.lucide.Sunset
import com.composables.icons.lucide.TriangleAlert
import com.composables.icons.lucide.Tag
import com.composables.icons.lucide.Trash2
import com.composables.icons.lucide.Save
import com.composables.icons.lucide.UserRound
import com.composables.icons.lucide.UserRoundPlus
import com.composables.icons.lucide.UsersRound
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.graphics.vector.path
import androidx.compose.ui.unit.dp

object PartejarIcons {
    object Navigation {
        val home = Lucide.House
        val gestantes = GestantesIcon
        val relacionamentos = Lucide.HeartHandshake
        val prontuario = Lucide.Clipboard
        val agenda = Lucide.CalendarDays
        val profile = Lucide.UserRound
        val workspace = Lucide.Building2
        val settings = Lucide.Settings
        val equipe = Lucide.UsersRound
        val planos = Lucide.Tag
    }

    object Actions {
        val add = Lucide.Plus
        val back = Lucide.ArrowLeft
        val more = Lucide.EllipsisVertical
        val search = Lucide.Search
        val notifications = Lucide.Bell
        val logout = Lucide.LogOut
        val lightMode = Lucide.Sun
        val darkMode = Lucide.Moon
        val showPassword = Lucide.Eye
        val hidePassword = Lucide.EyeOff
        val next = Lucide.ChevronRight
        val edit = Lucide.PencilLine
        val remove = Lucide.Trash2
        val save = Lucide.Save
        val deactivate = Lucide.CircleOff
        val activate = Lucide.CircleCheck
        val fingerprint = Lucide.FingerprintPattern
        val biometric: ImageVector get() = platformBiometricIcon
    }

    object Feedback {
        val success = Lucide.CircleCheck
        val attention = Lucide.TriangleAlert
        val info = Lucide.Info
        val error = Lucide.CircleAlert
    }

    object Clinical {
        val exams = Lucide.SquareActivity
        val calendar = Lucide.CalendarDays
        val schedule = Lucide.Clock
    }

    object Relationships {
        val capture = Lucide.UserRoundPlus
        val contact = Lucide.UserRoundPlus
        val meeting = Lucide.CalendarDays
        val waiting = Lucide.Clock
        val contract = Lucide.Handshake
        val lost = Lucide.CircleOff
        val followUp = Lucide.RefreshCw
        val whatsApp = Lucide.MessageCircle
        val instagram = Lucide.Instagram
        val other = Lucide.EllipsisVertical
        val baby = Lucide.UserRound
        val maternity = Lucide.Building2
        val dueDate = Lucide.CalendarDays
        val prenatal = Lucide.HeartHandshake
        val location = Lucide.House
        val origin = Lucide.UsersRound
    }

    object Contracts {
        val condition = Lucide.Tag
        val template = Lucide.FileText
        val signers = Lucide.UsersRound
        val patient = Lucide.UserRound
        val generate = Lucide.FileText
        val document = Lucide.FileText
        val view = Lucide.Eye
        val send = Lucide.Send
        val signature = Lucide.PencilLine
    }

    object Greeting {
        val morning = Lucide.Sunrise
        val afternoon = Lucide.Sunset
        val night = Lucide.Moon
    }
}

internal expect val platformBiometricIcon: ImageVector

private val GestantesIcon: ImageVector by lazy {
    ImageVector.Builder(
        name = "Gestantes",
        defaultWidth = 24.dp,
        defaultHeight = 24.dp,
        viewportWidth = 24f,
        viewportHeight = 24f
    ).apply {
        path(
            fill = null,
            stroke = SolidColor(Color.Black),
            strokeLineWidth = 2f,
            strokeLineCap = StrokeCap.Round,
            strokeLineJoin = StrokeJoin.Round
        ) {
            moveTo(10.2f, 6.5f)
            curveTo(11.3f, 6.5f, 12.2f, 5.6f, 12.2f, 4.5f)
            curveTo(12.2f, 3.4f, 11.3f, 2.5f, 10.2f, 2.5f)
            curveTo(9.1f, 2.5f, 8.2f, 3.4f, 8.2f, 4.5f)
            curveTo(8.2f, 5.6f, 9.1f, 6.5f, 10.2f, 6.5f)

            moveTo(9.5f, 8.3f)
            curveTo(7.5f, 9.5f, 6.8f, 12.2f, 7.9f, 14.7f)
            curveTo(9f, 17.3f, 11.9f, 18.6f, 14.4f, 17.7f)
            curveTo(16.6f, 16.9f, 17.7f, 14.5f, 16.9f, 12.2f)
            curveTo(16f, 9.9f, 13.6f, 8.4f, 11.2f, 8.4f)

            moveTo(7.9f, 14.7f)
            curveTo(7.6f, 16.6f, 8.2f, 18.5f, 9.6f, 20f)
        }
    }.build()
}
